// =============================================================================
// Origin UI — src/pages/admin/CreditCardDelegateAdminTab.tsx
// Admin Config → Credit Cards → Delegates tab.
// Delegates are additional approvers: "Siân can approve anything Sam approves."
// CC-11.3 v3
// =============================================================================

import { useState, useEffect, useMemo } from 'react';
import { api } from '@/services/apiService';
import { Card, ErrorBanner, LoadingSpinner, ConfirmDialog } from '@/components/common';
import {
  getDelegates, createDelegate, deleteDelegate,
  type CreditCardDelegate
} from '@/services/creditCardApi';
import styles from './admin.module.css';

interface EmployeeOption { employeeId: string; fullName: string; employeeNumber: string; }

type SortKey = 'delegateName' | 'approverName' | 'createdAt';
type SortDir = 'asc' | 'desc';

export function CreditCardDelegateAdminTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [delegates, setDelegates] = useState<CreditCardDelegate[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [formDelegate, setFormDelegate] = useState('');
  const [formApprover, setFormApprover] = useState('');

  // Sort
  const [sortKey, setSortKey] = useState<SortKey>('delegateName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterDelegate, setFilterDelegate] = useState('');
  const [filterApprover, setFilterApprover] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoading(true);
      const [d, e] = await Promise.all([getDelegates(), api.get<EmployeeOption[]>('/api/employees?minimal=true')]);
      setDelegates(d);
      setEmployees(e.map(emp => ({ ...emp, fullName: (((emp as any).firstName || '') + ' ' + ((emp as any).lastName || '')).trim() || emp.fullName || '' })).filter(emp => emp.fullName.trim() !== '').sort((a, b) => a.fullName.localeCompare(b.fullName)));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  const filteredDelegates = useMemo(() => {
    let data = delegates;
    if (filterDelegate) data = data.filter(d => (d.delegateName || '').toLowerCase().includes(filterDelegate.toLowerCase()));
    if (filterApprover) data = data.filter(d => ((d as any).approverName || '').toLowerCase().includes(filterApprover.toLowerCase()));
    return [...data].sort((a, b) => {
      const av = String((a as any)[sortKey] ?? '');
      const bv = String((b as any)[sortKey] ?? '');
      const cmp = av.localeCompare(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [delegates, filterDelegate, filterApprover, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }
  function sIcon(key: SortKey) { return sortKey !== key ? ' \u21D5' : sortDir === 'asc' ? ' \u2191' : ' \u2193'; }

  const hasFilters = filterDelegate || filterApprover;
  function clearFilters() { setFilterDelegate(''); setFilterApprover(''); }

  const delegateNames = useMemo(() => [...new Set(delegates.map(d => d.delegateName).filter(x => x != null && x !== ''))].sort(), [delegates]);
  const approverNames = useMemo(() => [...new Set(delegates.map(d => (d as any).approverName).filter(x => x != null && x !== ''))].sort() as string[], [delegates]);

  async function save() {
    try {
      setSaving(true);
      setError('');
      await createDelegate({ delegateEmployeeId: formDelegate, approverEmployeeId: formApprover } as any);
      setCreating(false);
      setFormDelegate('');
      setFormApprover('');
      await load();
    } catch (e: any) {
      const b = e.body ? JSON.parse(e.body) : null;
      setError(b?.error || b?.message || e.message);
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try { await deleteDelegate(deleteId); setDeleteId(null); await load(); }
    catch (e: any) { setError(e.message); }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 className={styles.formTitle}>
          Credit Card Delegates
          {hasFilters && <span style={{ fontSize: 12, color: 'var(--turquoise)', marginLeft: 8 }}>({filteredDelegates.length} of {delegates.length})</span>}
        </h4>
        <div style={{ display: 'flex', gap: 8 }}>
          {hasFilters && <button onClick={clearFilters} style={{ fontSize: 11, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Clear</button>}
          <button onClick={() => setShowFilters(!showFilters)} style={{ fontSize: 12, fontWeight: 600, padding: '6px 16px', borderRadius: 6, cursor: 'pointer', background: showFilters ? 'var(--turquoise-light)' : 'var(--gray-100)', color: showFilters ? 'var(--turquoise-dark)' : 'var(--gray-600)', border: '1px solid ' + (showFilters ? 'var(--turquoise)' : 'var(--gray-200)') }}>Filters</button>
          <button className={styles.saveBtn} onClick={() => { setCreating(true); setFormDelegate(''); setFormApprover(''); }}>+ Add Delegate</button>
        </div>
      </div>

      <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 16 }}>
        A delegate is an additional approver. If Siân is Sam's delegate, both Siân and Sam can approve reviews assigned to Sam. Only one needs to approve — it's either/or, not both.
      </p>

      {showFilters && (
        <div style={{ marginBottom: 12 }}>
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Delegate</label>
              <select className={styles.fieldInput} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }} value={filterDelegate} onChange={e => setFilterDelegate(e.target.value)}>
                <option value="">All</option>{delegateNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Approver</label>
              <select className={styles.fieldInput} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }} value={filterApprover} onChange={e => setFilterApprover(e.target.value)}>
                <option value="">All</option>{approverNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        </Card>
        </div>
      )}

      {creating && (
        <div style={{ marginBottom: 16 }}>
        <Card className={styles.formCard} padding={true}>
          <h4 className={styles.formTitle}>Add Delegate</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                Delegate (who can approve)
                {formDelegate && <span style={{ marginLeft: 8, color: 'var(--green)', fontSize: 13 }}>{'\u2705'}</span>}
              </label>
              <select className={styles.fieldInput} value={formDelegate} onChange={e => setFormDelegate(e.target.value)}>
                <option value="">Select delegate...</option>
                {employees.map(emp => <option key={emp.employeeId} value={emp.employeeId}>{emp.fullName} ({emp.employeeNumber})</option>)}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                Approver (on behalf of)
                {formApprover && <span style={{ marginLeft: 8, color: 'var(--green)', fontSize: 13 }}>{'\u2705'}</span>}
              </label>
              <select className={styles.fieldInput} value={formApprover} onChange={e => setFormApprover(e.target.value)}>
                <option value="">Select approver...</option>
                {employees.map(emp => <option key={emp.employeeId} value={emp.employeeId}>{emp.fullName} ({emp.employeeNumber})</option>)}
              </select>
            </div>
          </div>
          <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>
            This means the delegate can approve/return any review where the selected approver is the assigned approver.
          </p>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => { setCreating(false); setFormDelegate(''); setFormApprover(''); }}>Cancel</button>
            <button className={styles.saveBtn} onClick={save} disabled={saving || !formDelegate || !formApprover || formDelegate === formApprover}>
              {saving ? 'Adding...' : 'Add Delegate'}
            </button>
          </div>
        </Card>
        </div>
      )}

      {delegates.length === 0 && !creating ? (
        <Card><p style={{ padding: 20, textAlign: 'center', color: 'var(--gray-400)' }}>No delegates configured. Click "+ Add Delegate" to allow someone to approve on behalf of an approver.</p></Card>
      ) : (
        <Card padding={false}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('delegateName')}>Delegate{sIcon('delegateName')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('approverName')}>Can Approve For{sIcon('approverName')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('createdAt')}>Added{sIcon('createdAt')}</th>
                <th style={{ width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDelegates.map(d => (
                <tr key={d.creditCardDelegateId}>
                  <td style={{ fontWeight: 600 }}>{d.delegateName}</td>
                  <td>{(d as any).approverName || '\u2014'}</td>
                  <td>{new Date(d.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td><button className={styles.deleteBtn} onClick={() => setDeleteId(d.creditCardDelegateId)}>Remove</button></td>
                </tr>
              ))}
              {filteredDelegates.length === 0 && delegates.length > 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: 'var(--gray-400)' }}>No delegates match filter.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      )}

      <ConfirmDialog open={!!deleteId} title="Remove Delegate" message="Remove this delegate mapping? They will no longer be able to approve on behalf of this approver." confirmLabel="Remove" confirmVariant="danger" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
    </div>
  );
}
